import { createContext, useContext } from "react";
import { useStore } from "zustand";
import type { Dataset, DesktopUpdateState, OperationFactory, SyncOperation } from "@otrack/domain";
import type { SyncStatus } from "@otrack/local-store";
import type { AssistantCall } from "./assistant";
import type { GoogleCalendarSync, GoogleServices } from "./calendar/google";
import type { PendingChange } from "./calendar/google-events";
import type { AppStore } from "./store";

/** How this device syncs. "local" means sync is not configured on this build. */
export interface SyncConnection {
  mode: "local" | "signed-out" | "signed-in";
  status: SyncStatus;
  account: string | null;
  signIn?: () => void;
  signOut?: () => void;
  syncNow?: () => void;
}

/** What the desktop tray shows: the running Timer, what to resume, and Projects to start. */
export interface DesktopTimerState {
  /** `task` names the running Timer's open Task, which Done completes. */
  running: { label: string; startedAt: number; task: string | null } | null;
  resume: { label: string } | null;
  recent: Array<{ projectId: string; label: string }>;
}

export type DesktopPage = "today" | "calendar" | "projects" | "invoices" | "settings";

/** Something asked for from the tray, the menu, or a global shortcut. */
export type DesktopCommand =
  | { type: "stop" }
  /** Stops the running Timer and completes its Task. */
  | { type: "done" }
  | { type: "resume" }
  | { type: "start"; projectId: string }
  /** Stops the running Timer, or resumes the last entry. */
  | { type: "toggle" }
  | { type: "command-bar" }
  | { type: "go"; page: DesktopPage };

export interface DesktopShortcut { label: string; keys: string; registered: boolean }

/** What only the desktop app offers: its tray and shortcuts, launch at login, and updates. */
export interface DesktopServices {
  version: string;
  updates: DesktopUpdateState;
  checkForUpdates: () => void;
  installUpdate: () => void;
  /** Null where this system can't start ontrack at login. */
  openAtLogin: boolean | null;
  setOpenAtLogin: (enabled: boolean) => void;
  shortcuts: DesktopShortcut[];
  onCommand: (listener: (command: DesktopCommand) => void) => () => void;
  publishTimer: (state: DesktopTimerState) => void;
}

/** Platform services the shell uses; the web and desktop apps provide their own. */
export interface Services {
  sync: SyncConnection;
  /** Null when the assistant cannot be reached (signed out or not configured). */
  assistant: AssistantCall | null;
  /** Google Calendar through ontrack's server; null when signed out or without a server. */
  google?: GoogleServices | null;
  /** Present only in the desktop app. */
  desktop?: DesktopServices | null;
}

export interface Toast { id: number; message: string; undoable: boolean }

/** Text handed to the Command Bar from elsewhere, like the Command Palette. */
export interface CommandRequest { id: number; text: string; run: boolean }

export interface AppContextValue {
  store: AppStore;
  now: number;
  services: Services;
  /**
   * Applies operations and shows a toast; undoable changes get an Undo button. `google` changes
   * go to Google Calendar along with them, and Undo sends their reverse.
   */
  act: (operations: SyncOperation[], message: string, options?: { undoable?: boolean; google?: PendingChange[] }) => Promise<void>;
  /** Google Calendar on this device; null where it isn't available. */
  google: GoogleCalendarSync | null;
  notify: (message: string) => void;
  openEntry: (entryId: string) => void;
  /** Opens the new-project dialog, optionally with a Client already chosen. */
  openNewProject: (clientId?: string) => void;
  openNewClient: () => void;
  /** Opens the new-invoice dialog, optionally with a Client already chosen. */
  openNewInvoice: (clientId?: string) => void;
  openImport: () => void;
  openPalette: () => void;
  commandRequest: CommandRequest | null;
  /** Goes to Today and puts text in the Command Bar, running it when `run` is set. */
  sendToCommandBar: (text: string, run: boolean) => void;
  /** Called by the Command Bar once it has taken the request. */
  clearCommandRequest: () => void;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp must be used inside <OTrackApp>");
  return value;
}

export function useDataset(): Dataset {
  return useStore(useApp().store, (state) => state.dataset);
}

export function useFactory(): OperationFactory {
  return useApp().store.getState().factory;
}
